import React, { Dispatch, FC, SetStateAction, useState } from 'react';
import { Gear } from '@gravity-ui/icons';
import { Button, Icon, Popup, Switch, Select } from '@gravity-ui/uikit';
import classes from '../styles/UserSettings.module.css';
import { UserSettings } from '../../../types/user/UserSettings';
import { UserService } from '../../../api/api-services/user-service/userService';
import { toastError, toastSuccess } from '../../../libs/toasts/toasts';
import { vzhikLogger } from '../../../libs/vzhik-logger/VzhikLogger';
import { UploadSDK } from '@yandex-int/video-upload-sdk';
import { Copy } from '@gravity-ui/icons';
import { commonT, toastsT } from '../../../i18n';
import { setLocale, getCurrentLocale } from '../../../i18n/intl';

type UserSettingsCardProps = {
    settings: UserSettings;
    setSettings: Dispatch<SetStateAction<UserSettings>>;
};

export const UserSettingsCard: FC<UserSettingsCardProps> = ({ settings, setSettings }) => {
    const [showSettings, setShowSettings] = useState<boolean>(false);
    const buttonRef = React.useRef(null);

    const languageOptions = [
        { value: 'ru', content: commonT('russianShort') },
        { value: 'en', content: commonT('englishShort') },
    ];

    const currentLocale = getCurrentLocale();

    const updateNotificationsType = async (checked: boolean) => {
        try {
            const newType = checked ? 'EMAIL' : 'DISABLE';

            await UserService.updateNotificationsType(newType);

            setSettings((settings) => ({
                ...settings,
                notifications_type: newType,
            }));
        } catch (e) {
            toastError.open(toastsT('updateNotificationsFailed'));
            vzhikLogger.logError({
                message: 'Не удалось изменить настройки оповещений',
                type: 'server',
                additional: {
                    error: e,
                },
            });
        }
    };

    const copyVsidToClipboard = () => {
        navigator.clipboard.writeText(UploadSDK.vsid);
        toastSuccess.open(commonT('copiedToClipboard'));
        setShowSettings(false);
    };

    const handleLanguageChange = (value: string[]) => {
        if (value.length > 0) {
            setLocale(value[0]);
        }
    };

    return (
        <>
            <Button
                ref={buttonRef}
                size="s"
                view="flat"
                onClick={() => setShowSettings((show) => !show)}
            >
                <Icon data={Gear} size={20} />
            </Button>
            <Popup
                anchorRef={buttonRef}
                open={showSettings}
                placement="left-start"
                onOutsideClick={() => setShowSettings(false)}
            >
                <ul className={classes['user-settings']}>
                    <li className={classes['user-settings__item']}>
                        <span className={classes['user-settings__label']}>
                            {commonT('language')}
                        </span>
                        <div className={classes['user-settings__switch']}>
                            <Select
                                size="s"
                                value={[currentLocale]}
                                options={languageOptions}
                                onUpdate={handleLanguageChange}
                            />
                        </div>
                    </li>
                    <li className={classes['user-settings__item']}>
                        <span className={classes['user-settings__label']}>
                            {commonT('emailNotifications')}
                        </span>
                        <Switch
                            size="m"
                            className={classes['user-settings__switch']}
                            checked={settings && settings.notifications_type === 'EMAIL'}
                            onUpdate={updateNotificationsType}
                        ></Switch>
                    </li>
                    <li className={classes['user-settings__item']}>
                        <span className={classes['user-settings__label']}>
                            {commonT('copyVsid')}
                        </span>
                        <div className={classes['user-settings__switch']}>
                            <Button size="s" view="flat" onClick={copyVsidToClipboard}>
                                <Icon data={Copy} />
                            </Button>
                        </div>
                    </li>
                </ul>
            </Popup>
        </>
    );
};
